import {
  BookMarked,
  Building,
  Building2,
  ClipboardList,
  FileText,
  IdCard,
  Landmark,
  MapPin,
  Receipt,
  Route,
  Shield,
  ShieldAlert,
  Truck,
  Users,
  Wrench,
  type LucideIcon,
} from 'lucide-react';

export interface OfficeSection {
  /** Route segment under `/`. */
  slug: string;
  label: string;
  icon: LucideIcon;
  group: 'Operations' | 'Assets' | 'Money' | 'Admin';
  /** One line on what the office does here — shown in the page header. */
  summary: string;
  /** What the section will hold, from docs/technical-design.md → Modules. Shown until the screen is built. */
  covers: string[];
  /** The permission that gates this section in the nav — a section visible to any one of an array — server remains the authority, this only hides the link (CLAUDE.md → Web conventions). */
  permission: string | string[];
}

/** The office nav. The router, the sidebar and each section's page header all read from this list. */
export const OFFICE_SECTIONS: OfficeSection[] = [
  {
    slug: 'dispatch',
    label: 'Dispatch',
    icon: Route,
    group: 'Operations',
    summary: 'Assign trucks and drivers to jobs, and see what is moving today.',
    covers: ['Trips and assignment', 'Eligibility rules', 'Overrides with a reason'],
    permission: 'dispatch.view',
  },
  {
    slug: 'jobs',
    label: 'Jobs',
    icon: ClipboardList,
    group: 'Operations',
    summary: 'Customer jobs from booking to delivery.',
    covers: ['Cargo and documents', 'Deadlines', 'Job states'],
    permission: 'job.view',
  },
  {
    slug: 'tracking',
    label: 'Tracking',
    icon: MapPin,
    group: 'Operations',
    summary: 'Live positions, geofences and alerts from the GPS provider.',
    covers: ['Live fleet map', 'Geofences', 'Alert rules', 'Trip events'],
    permission: 'tracking.view',
  },
  {
    slug: 'fleet',
    label: 'Fleet',
    icon: Truck,
    group: 'Assets',
    summary: 'Trucks, trailers and everything that keeps them on the road.',
    covers: ['Trucks and trailers', 'Documents and expiry', 'Tyres'],
    permission: 'fleet.view',
  },
  {
    slug: 'drivers',
    label: 'Drivers',
    icon: IdCard,
    group: 'Assets',
    summary: 'Driver profiles, licences and compliance documents.',
    covers: ['Driver records', 'Licence and hazmat certification', 'Employment status', 'Documents'],
    permission: 'driver.view',
  },
  {
    slug: 'compliance',
    label: 'Compliance',
    icon: ShieldAlert,
    group: 'Assets',
    summary: 'Expiring, expired and missing documents across the fleet.',
    covers: ['Fleet-wide compliance list', 'Count tile data'],
    permission: ['fleet.view', 'driver.view'],
  },
  {
    slug: 'maintenance',
    label: 'Maintenance',
    icon: Wrench,
    group: 'Assets',
    summary: 'Work orders, defects and service schedules.',
    covers: ['Work orders', 'Defects', 'Service schedules'],
    permission: 'maintenance.view',
  },
  {
    slug: 'expenses',
    label: 'Expenses',
    icon: Receipt,
    group: 'Money',
    summary: 'Driver advances, trip expenses and approvals.',
    covers: ['Advances', 'Approvals', 'Reconciliation'],
    permission: 'expense.view',
  },
  {
    slug: 'billing',
    label: 'Billing',
    icon: FileText,
    group: 'Money',
    summary: 'Invoices, credit notes, payments and ageing.',
    covers: ['eTIMS invoices', 'Credit notes', 'Payments', 'Ageing'],
    permission: 'billing.view',
  },
  {
    slug: 'customers',
    label: 'Customers',
    icon: Building2,
    group: 'Money',
    summary: 'Customers, credit limits and billing preferences.',
    covers: ['Customer records', 'Credit limits', 'Billing preferences'],
    permission: 'customer.view',
  },
  {
    slug: 'roles',
    label: 'Roles',
    icon: Shield,
    group: 'Admin',
    summary: 'Every role and the permissions it grants.',
    covers: ['Roles and their permissions'],
    permission: 'configuration.manage',
  },
  {
    slug: 'users',
    label: 'Users',
    icon: Users,
    group: 'Admin',
    summary: 'Office users, their roles and status.',
    covers: ['Create and deactivate users', 'Role assignment'],
    permission: 'user.manage',
  },
  {
    slug: 'approval-thresholds',
    label: 'Approval thresholds',
    icon: Landmark,
    group: 'Admin',
    summary: 'Who approves what, by amount.',
    covers: ['Approval kinds and their ladders', 'The Admin ceiling'],
    permission: 'threshold.manage',
  },
  {
    slug: 'reference-data',
    label: 'Reference data',
    icon: BookMarked,
    group: 'Admin',
    summary: 'The fleet vocabulary trucks, trailers and documents are described with.',
    covers: ['Truck classes', 'Trailer types', 'Document types', 'Job types'],
    permission: 'configuration.manage',
  },
  {
    slug: 'company',
    label: 'Company',
    icon: Building,
    group: 'Admin',
    summary: 'The company this deployment serves: its particulars, tax registration and locale.',
    covers: ['Company particulars', 'Tax registration', 'Locale settings'],
    permission: 'configuration.manage',
  },
];

export const OFFICE_SECTION_GROUPS = ['Operations', 'Assets', 'Money', 'Admin'] as const;
